import { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Snackbar,
  Alert,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { UserList } from './components/UserList';
import { UserListFilters } from './components/UserListFilters';
import { UserCreateDialog } from './components/UserCreateDialog';
import { UserEditDialog } from './components/UserEditDialog';
import { ResetPasswordDialog } from './components/ResetPasswordDialog';
import { useHasAnyRole } from '@/store/useAuthStore';
import type { UserListItem, UserQueryParams } from './types/user.types';

export function UsersPage() {
  const canManageUsers = useHasAnyRole('admin', 'user-manager');

  const [filters, setFilters] = useState<UserQueryParams>({});
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editUserId, setEditUserId] = useState<string | null>(null);
  const [resetPasswordUser, setResetPasswordUser] = useState<{
    id: string;
    email: string;
  } | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const handleEdit = useCallback((user: UserListItem) => {
    setEditUserId(user.id);
  }, []);

  const handleResetPassword = useCallback((user: UserListItem) => {
    setResetPasswordUser({ id: user.id, email: user.email });
  }, []);

  const showSuccess = (message: string) => {
    setSnackbar({ open: true, message, severity: 'success' });
  };

  const handleCreateSuccess = () => {
    showSuccess('User created successfully');
  };

  const handleEditSuccess = () => {
    showSuccess('User updated successfully');
  };

  const handleResetPasswordSuccess = () => {
    showSuccess('Password reset successfully');
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          Users
        </Typography>
        {canManageUsers && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create User
          </Button>
        )}
      </Box>

      <Paper sx={{ p: 2 }}>
        <UserListFilters filters={filters} onFiltersChange={setFilters} />
        <UserList
          filters={filters}
          onEdit={handleEdit}
          onResetPassword={handleResetPassword}
          canManageUsers={canManageUsers}
        />
      </Paper>

      {/* Dialogs */}
      <UserCreateDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      <UserEditDialog
        userId={editUserId}
        open={!!editUserId}
        onClose={() => setEditUserId(null)}
        onSuccess={handleEditSuccess}
      />

      <ResetPasswordDialog
        userId={resetPasswordUser?.id ?? null}
        userEmail={resetPasswordUser?.email ?? null}
        open={!!resetPasswordUser}
        onClose={() => setResetPasswordUser(null)}
        onSuccess={handleResetPasswordSuccess}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
